import React, { useState, useEffect } from "react";
import {
  Form, Input, Select, InputNumber, DatePicker,
  Row, Col, Spin, message, Upload, Button,
} from "antd";
import { PaperClipOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";

const { Option } = Select;

// ── Helper FireDAC robusto (idêntico ao usado em EmissaoUsoProprioVenda) ──
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

// ── Serialização para o Delphi (mesmas regras do módulo de Vendas) ───────
// StrToCurr no Delphi (locale pt-BR) exige vírgula decimal: "1234,56"
const toDelphiCurr = (v) => {
  const n = Number(v) || 0;
  return n.toFixed(2).replace(".", ",");
};

// StrToInt no Delphi não aceita pontos de milhar ("9.813.000" EXPLODE)
const toDelphiInt = (v) => String(Math.round(Number(v) || 0));

// ⚠️ NOTA IMPORTANTE (corrigida): diferente do backend de Vendas, o backend
// de Hospedagem (PostInserirHospedagem) NÃO multiplica ValorUnitario por 1000
// — ele usa `vPrecoMilheiro := vValorUnitario` direto, sem transformação.
// Ou seja, aqui ValorUnitario precisa ser enviado como custo POR MILHEIRO
// (o mesmo valor exibido em "Custo Médio", ex: R$14,36), com formatação de
// moeda normal (toDelphiCurr) — NÃO dividir por 1000 como se fazia antes.
// A função toDelphiFloat(casas=6) foi removida por não ser mais necessária
// neste arquivo; se algum dia o backend de Hospedagem passar a esperar valor
// por milha (fração), ela precisará voltar.

// Tipo da operação (campo TipoOperacao), derivado da origem das milhas.
// ⚠️ ATENÇÃO: os valores abaixo são um PALPITE baseado no padrão usado em Vendas.
// "FRENTE VOO" é jargão de emissão aérea e não faz sentido para hospedagem.
// CONFIRME com o backend/DBA qual string o procedure InserirHospedagem espera
// (ou se ele sequer lê esse campo) antes de considerar isso definitivo.
const TIPO_OPERACAO_HOSPEDAGEM = {
  proprias:      "HOSPEDAGEM PROPRIA",
  balcao:        "HOSPEDAGEM BALCÃO DE MILHAS",
  consolidadora: "HOSPEDAGEM CONSOLIDADORA",
};

// ── InputNumber: formatter/parser padrão brasileiro (copiado do módulo de Vendas) ──
const fmtBRL = (v) => {
  if (v === "" || v === undefined || v === null) return "";
  const str = String(v);

  if (str.includes(",")) {
    const [intPart, decPart] = str.split(",");
    const intClean = intPart.replace(/\./g, "");
    const intFmt   = intClean.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return decPart !== undefined ? `${intFmt},${decPart}` : intFmt;
  }

  if (str.includes(".")) {
    const partes      = str.split(".");
    const ultimaParte = partes[partes.length - 1];

    if (ultimaParte.length === 3) {
      const inteiro = str.replace(/\./g, "");
      return inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }

    const [intPart, decPart] = str.split(".");
    const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${intFmt},${decPart}`;
  }

  return str.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const parseBRL = (v) => {
  if (!v && v !== 0) return "";
  const str = String(v).trim();
  if (!str) return "";

  const hasComa = str.includes(",");
  const hasDot  = str.includes(".");

  let normalized;

  if (hasComa && hasDot) {
    normalized = str.replace(/\./g, "").replace(",", ".");
  } else if (hasComa) {
    normalized = str.replace(",", ".");
  } else if (hasDot) {
    const partes = str.split(".");
    const ultimaParte = partes[partes.length - 1];
    if (ultimaParte.length === 3) {
      normalized = str.replace(/\./g, "");
    } else {
      normalized = str.replace(/\.(?=.*\.)/g, "");
    }
  } else {
    normalized = str;
  }

  const n = parseFloat(normalized);
  return isNaN(n) ? "" : n;
};

// Versão para campos de quantidade inteira (milhas) — sem decimais
const fmtMilhas = (v) => {
  if (v === "" || v === undefined || v === null) return "";
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const parseMilhas = (v) => {
  if (!v) return 0;
  const n = parseInt(String(v).replace(/\./g, ""), 10);
  return isNaN(n) ? 0 : n;
};

// ── Logos programas com cores exatas (idêntico ao módulo de Vendas) ─────
const LOGOS_MAP = [
  // ── Nacionais ──────────────────────────────────────────────────────
  { keys: ["livelo"],                    bg: "#e8008b", letra: "LV"  },
  { keys: ["itaú pontos","itau pontos"], bg: "#d48c0c", letra: "IT"  },
  { keys: ["smiles"],                    bg: "#ff6600", letra: "S"   },
  { keys: ["azul fidelidade","azul"],    bg: "#003087", letra: "A"   },
  { keys: ["coopera pj"],                bg: "#007b5e", letra: "CPJ" },
  { keys: ["coopera"],                   bg: "#007b5e", letra: "COO" },
  { keys: ["latam"],                     bg: "#d81a36", letra: "L"   },
  { keys: ["tap miles","tap"],           bg: "#009900", letra: "TAP" },
  { keys: ["max milhas"],                bg: "#1e3a6e", letra: "MM"  },
  { keys: ["hotmilhas"],                 bg: "#cc3300", letra: "HM"  },
  { keys: ["ipiranga"],                  bg: "#f5a800", letra: "IP"  },
  { keys: ["esfera empresas"],           bg: "#8b0000", letra: "EE"  },
  { keys: ["esfera"],                    bg: "#8b0000", letra: "E_"  },
  { keys: ["bank milhas"],               bg: "#0066cc", letra: "BM"  },
  { keys: ["cash milhas"],               bg: "#5a5a5a", letra: "CM"  },
  { keys: ["curtaí","curtai"],           bg: "#e8a000", letra: "CT"  },
  { keys: ["compro suas milhas"],        bg: "#1e3a4a", letra: "CSM" },
  { keys: ["átomos","atomos"],           bg: "#000000", letra: "ÁT"  },
  { keys: ["gpa"],                       bg: "#00aa44", letra: "GPA" },
  { keys: ["pontos caixa"],              bg: "#007b5e", letra: "PC"  },
  { keys: ["caixa empresas"],            bg: "#007b5e", letra: "CE"  },
  { keys: ["sicredi empresarial"],       bg: "#009900", letra: "SCE" },
  { keys: ["sicredi"],                   bg: "#009900", letra: "SC"  },
  { keys: ["dotz"],                      bg: "#ff6600", letra: "DZ"  },
  { keys: ["porto plus"],                bg: "#0057a8", letra: "PP"  },
  { keys: ["inter loop"],                bg: "#ff6b00", letra: "IL"  },
  { keys: ["inter empresas"],            bg: "#ff6b00", letra: "IE"  },
  { keys: ["volare"],                    bg: "#c8102e", letra: "VL"  },
  { keys: ["membership rewards","amex"], bg: "#006fcf", letra: "MR"  },
  { keys: ["safra rewards"],             bg: "#003366", letra: "SR"  },
  { keys: ["suma"],                      bg: "#1e3a4a", letra: "SU"  },
  { keys: ["cressol"],                   bg: "#007b5e", letra: "CR"  },
  { keys: ["banrisul"],                  bg: "#003087", letra: "BR"  },
  { keys: ["unicred"],                   bg: "#009900", letra: "UC"  },
  { keys: ["milhas plus"],               bg: "#e8a000", letra: "M+"  },
  { keys: ["xp pontos"],                 bg: "#1a1a2e", letra: "XP"  },
  { keys: ["nubank"],                    bg: "#820ad1", letra: "NU"  },
  { keys: ["ailos"],                     bg: "#007b5e", letra: "AI"  },
  { keys: ["banestes"],                  bg: "#003087", letra: "BN"  },
  { keys: ["bb empresas"],               bg: "#f5a800", letra: "BB"  },
  { keys: ["itaú empresas","itau empresas"], bg: "#d48c0c", letra: "ITE" },
  { keys: ["pontos btg"],                bg: "#1a1a2e", letra: "BTG" },

  // ── Internacionais ──────────────────────────────────────────────────
  { keys: ["iberia"],                    bg: "#cc0000", letra: "IB"  },
  { keys: ["mileageplus","united"],      bg: "#002244", letra: "UA"  },
  { keys: ["all accor","accor"],         bg: "#6a628a", letra: "AL"  },
  { keys: ["Qantas","qantas"],           bg: "#c33333", letra: "QT"},
  { keys: ["ihg"],                       bg: "#6a0dad", letra: "IHG" },
  { keys: ["aadvantage","american"],     bg: "#0078d2", letra: "AA"  },
  { keys: ["flying blue"],               bg: "#003087", letra: "FB"  },
  { keys: ["british airways"],           bg: "#2b4090", letra: "BA"  },
  { keys: ["qatar"],                     bg: "#5c0632", letra: "QR"  },
  { keys: ["aeroplan"],                  bg: "#c8102e", letra: "AP"  },
  { keys: ["copa","connectmiles"],       bg: "#0033a0", letra: "CM"  },
  { keys: ["delta"],                     bg: "#c8102e", letra: "DL"  },
  { keys: ["emirates"],                  bg: "#c8102e", letra: "EK"  },
  { keys: ["virgin atlantic","flying club"], bg: "#e8003d", letra: "VS" },
  { keys: ["finnair"],                   bg: "#003580", letra: "AY"  },
  { keys: ["wyndham"],                   bg: "#003087", letra: "WY"  },
];

const getLogo = (nome = "") => {
  const n = nome.toLowerCase();
  for (const l of LOGOS_MAP) {
    if (l.keys.some((k) => n.includes(k))) return l;
  }
  return { bg: "#607d8b", letra: (nome || "??").slice(0, 2).toUpperCase() };
};

const LogoCircle = ({ nome, size = 20 }) => {
  const { bg, letra } = getLogo(nome);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: bg, color: "#fff",
      fontSize: size * 0.33, fontWeight: 700, flexShrink: 0,
      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    }}>{letra}</span>
  );
};

export default function EmissaoHospedagem({ clienteId: clienteIdProp }) {
  const [form] = Form.useForm();
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [cartoes, setCartoes]     = useState([]);
  const [titulares, setTitulares] = useState([]);
  const [clienteId, setClienteId] = useState(null);

  // Origem das milhas
  const [origemMilhas, setOrigemMilhas]               = useState("proprias");
  const [nomeFornecedor, setNomeFornecedor]           = useState("");
  const [contatoFornecedor, setContatoFornecedor]     = useState("");
  const [custoMilheiroBalcao, setCustoMilheiroBalcao] = useState(0);
  const [nomeConsolidadora, setNomeConsolidadora]     = useState("");
  const [precoConsolidadora, setPrecoConsolidadora]   = useState(0);

  const [cartaoSel, setCartaoSel] = useState(null);

  // Campos numéricos
  const [precoReservaPagante, setPrecoReservaPagante] = useState(0);
  const [milhasUtil, setMilhasUtil]     = useState(0);
  const [taxas, setTaxas]               = useState(0);
  const [extras, setExtras]             = useState(0);
  const [fileList, setFileList]         = useState([]);

  // Hóspedes (input livre, sem busca no banco — diferente de Vendas,
  // que busca passageiros já cadastrados. Mantido por design.)
  const [nomeHospedeInput, setNomeHospedeInput] = useState("");
  const [hospedesSel, setHospedesSel]           = useState([]);
  const [erroHospede, setErroHospede]           = useState(false);

  // ── Cálculos derivados ──────────────────────────────────────────────
  // custoMedio = R$ por MILHEIRO do cartão selecionado (ex: 18,00 = R$18/1.000 milhas)
  const custoMedio  = cartaoSel ? Number(cartaoSel.car_vunitario ?? 0) : 0;
  const saldoDisp   = cartaoSel ? Number(cartaoSel.car_saldo_milhas ?? 0) : 0;
  const cpfsLivres  = cartaoSel ? Number(cartaoSel.car_qtde_cpf ?? 1) : 0;

  // custoCalc = custo do MILHEIRO para a operação atual, considerando a origem
  const custoCalc = origemMilhas === "balcao" ? custoMilheiroBalcao : custoMedio;

  // precoEmMilhas = R$ real gasto nas milhas
  const precoEmMilhas = custoCalc > 0 && milhasUtil > 0
    ? parseFloat(((custoCalc / 1000) * milhasUtil).toFixed(2))
    : 0;

  // Na consolidadora não se "gasta milhas": o custo é o preço fechado com ela.
  const baseDesconto = origemMilhas === "consolidadora" ? precoConsolidadora : precoEmMilhas;

  // Custo real da operação = custo das milhas (ou consolidadora) + taxas + extras.
  const custoTotalOperacao = parseFloat((baseDesconto + taxas + extras).toFixed(2));

  const economiaGerada = precoReservaPagante > 0 && baseDesconto > 0
    ? parseFloat((precoReservaPagante - custoTotalOperacao).toFixed(2))
    : 0;

  // ⚠️ CORRIGIDO: antes gatilhava só em "economiaGerada > 0", o que ZERAVA
  // silenciosamente a porcentagem em qualquer operação com prejuízo
  // (economia negativa) — o valor monetário ia negativo pro backend, mas a
  // porcentagem ia 0,00, uma inconsistência que quebra relatórios que cruzam
  // os dois campos. Agora reflete corretamente também o prejuízo (% negativa).
  const economiaPct = precoReservaPagante > 0 && economiaGerada !== 0
    ? parseFloat(((economiaGerada / precoReservaPagante) * 100).toFixed(2))
    : 0;

  // ── Resolve clienteId ──────────────────────────────────────────────────
  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try { const u = JSON.parse(localStorage.getItem("user") || "{}"); id = u.cli_id || u.id; } catch (e) {}
    }
    if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    if (id) { setClienteId(id); carregarDados(id); }
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  const carregarDados = async (id) => {
    setLoading(true);
    try {
      // 1. Busca todos os titulares do gestor
      const titRes  = await api.get("/ServerPrincipal/PesquisaTitular", { params: { Cliente: id } });
      const titRows = extractRows(titRes.data).map(normalize);

      // 2. Deduplica titulares pelo cli_id
      const seen = new Set();
      const titDeduplic = titRows.filter((t) => {
        const k = String(t.cli_id ?? "");
        if (!k || k === "0" || seen.has(k)) return false;
        seen.add(k); return true;
      });

      const titFinais = titDeduplic.length > 0
        ? titDeduplic
        : [{ cli_id: id, cli_nome: "Titular Principal" }];

      // 3. Busca cartões de CADA titular individualmente
      const carResArr = await Promise.all(
        titFinais.map((t) =>
          api.get("/ServerPrincipal/PesquisaCartoes", { params: { Cliente: t.cli_id } })
            .then((res) => ({ rows: extractRows(res.data).map(normalize), tit: t }))
            .catch(() => ({ rows: [], tit: t }))
        )
      );

      // 4. Monta lista flat de cartões enriquecidos com dados do titular
      const cartoesFlat = [];
      const seenCar = new Set();
      carResArr.forEach(({ rows, tit }) => {
        rows.forEach((c) => {
          const carKey = String(c.car_id ?? "");
          if (!carKey || carKey === "0" || seenCar.has(carKey)) return;
          seenCar.add(carKey);
          cartoesFlat.push({
            car_id:            c.car_id ?? 0,
            car_nome_programa: c.car_nome_programa ?? "",
            car_saldo_milhas:  Number(c.car_saldo_milhas ?? 0),
            car_vunitario:     Number(c.car_vunitario    ?? 0),
            car_qtde_cpf:      Number(c.car_qtde_cpf     ?? 0),
            cli_id:            tit.cli_id,
            cli_nome:          tit.cli_nome ?? "",
            cli_email:         tit.cli_email ?? "",
            cli_cpf:           tit.cli_cpf ?? "",
            cli_telefone:      tit.cli_telefone ?? "",
          });
        });
      });

      setTitulares(titFinais);
      setCartoes(cartoesFlat);
    } catch (e) {
      message.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleCartaoChange = (id) => {
    const c = cartoes.find((x) => x.car_id === id);
    setCartaoSel(c || null);
  };

  const handleOrigemChange = (v) => {
    setOrigemMilhas(v);
    setCartaoSel(null);
    setNomeFornecedor(""); setContatoFornecedor(""); setCustoMilheiroBalcao(0);
    setNomeConsolidadora(""); setPrecoConsolidadora(0);
    form.setFieldValue("cartaoId", undefined);
  };

  // ── Hóspedes (digitação livre) ─────────────────────────────────────────
  const adicionarHospede = () => {
    const nome = nomeHospedeInput.trim();
    if (!nome) return;
    if (hospedesSel.find((h) => h.nome.toLowerCase() === nome.toLowerCase())) {
      setNomeHospedeInput("");
      return;
    }
    setHospedesSel([...hospedesSel, { id: Date.now(), nome }]);
    setNomeHospedeInput("");
    setErroHospede(false);
  };

  const removerHospede = (id) => {
    setHospedesSel(hospedesSel.filter((h) => h.id !== id));
  };

  const handleHospedeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      adicionarHospede();
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    if (!clienteId) { message.error("Cliente não identificado."); return; }
    if (!values.voucherReserva) { message.error("Voucher da Reserva é obrigatório."); return; }
    if (!cartaoSel) { message.error("Selecione a conta/programa."); return; }
    // Consolidadora não usa milhas — só cobra validação de milhas fora desse caso.
    // (No módulo de Vendas essa validação roda incondicionalmente e trava o
    // submit quando a origem é Consolidadora — corrigido aqui de propósito.)
    if (origemMilhas !== "consolidadora" && (!milhasUtil || milhasUtil <= 0)) {
      message.error("Informe as milhas utilizadas.");
      return;
    }
    if (hospedesSel.length === 0) {
      setErroHospede(true);
      message.error("Inclua pelo menos um hóspede.");
      return;
    }

    setSaving(true);
    try {
      /*
       * CONTRATO COM O BACKEND DELPHI (InserirHospedagem) — confirmado lendo
       * o PostInserirHospedagem real, e é DIFERENTE do de InserirVendas:
       *
       * QtdeMilhas    → StrToInt()  → nunca enviar com ponto de milhar
       * ValorUnitario → o backend NÃO multiplica por 1000 (vPrecoMilheiro :=
       *                 vValorUnitario, direto) → enviamos o custo POR
       *                 MILHEIRO (o mesmo valor de "Custo Médio"), não por milha.
       * Economia/Taxa/Extras → StrToCurr() → decimal com VÍRGULA
       */
      const tipoOperacao = TIPO_OPERACAO_HOSPEDAGEM[origemMilhas] || "HOSPEDAGEM";

      const payload = {
        IDCliente:       clienteId,
        IDCartao:        cartaoSel.car_id,

        QtdeMilhas:      toDelphiInt(milhasUtil),

        // ← CORRIGIDO: antes dividia por 1000 (padrão copiado de Vendas, que
        //   não se aplica aqui) e o backend gravava R$0,01 em vez de R$14,36.
        //   custoCalc já é o custo por milheiro — envia direto.
        ValorUnitario:   toDelphiCurr(custoCalc),

        // ← NOVO — confirme com o backend se esse campo é lido/esperado (ver nota no topo do arquivo)
        TipoOperacao:    tipoOperacao,

        ValorTotal:      toDelphiCurr(precoReservaPagante),
        ValorReserva:    toDelphiCurr(precoReservaPagante),
        ValorCusto:      toDelphiCurr(baseDesconto),
        ValorMilhas:     toDelphiCurr(precoEmMilhas),
        ValorTaxa:       toDelphiCurr(taxas),

        // ← NOVO: antes "extras" nunca era enviado ao servidor (dado descartado)
        ValorExtras:     toDelphiCurr(extras),

        FormaPagamento:  origemMilhas,
        Observacoes:     values.observacoes || "",
        Status:          1,
        QtdeCpf:         String(hospedesSel.length),

        // ← CORRIGIDO: agora usa a mesma serialização (vírgula) do módulo de Vendas
        Economia:        toDelphiCurr(economiaGerada),
        PEconomia:       toDelphiCurr(economiaPct),

        VoucherReserva:  values.voucherReserva || "",
        NomeHospedagem:  values.nomeHospedagem || "",
        ObjetivoEmissao: values.objetivoEmissao || "uso_proprio",
        DataCheckIn:     values.dataCheckIn ? values.dataCheckIn.format("DD/MM/YYYY HH:mm:ss") : "",

        // ← NOVO: data de check-out. Mesmo formato do check-in (StrToDateTime no backend).
        //   ⚠️ Backend: o else do DataCheckOut atribui a vDataCheckIn por engano
        //   (deveria ser vDataCheckOut). E, como o check-in, mandar string vazia
        //   faz StrToDateTime('') estourar. Preencha o campo ou trate no backend.
        DataCheckOut:    values.dataCheckOut ? values.dataCheckOut.format("DD/MM/YYYY HH:mm:ss") : "",
      };

      console.log("[InserirHospedagem] payload →", payload);

      await api.post("/ServerPrincipal/InserirHospedagem", payload);

      message.success("Reserva de hospedagem registrada com sucesso!");
      form.resetFields();
      setPrecoReservaPagante(0); setMilhasUtil(0); setTaxas(0); setExtras(0);
      setCartaoSel(null); setHospedesSel([]); setFileList([]);
      setOrigemMilhas("proprias");
      setNomeFornecedor(""); setContatoFornecedor(""); setCustoMilheiroBalcao(0);
      setNomeConsolidadora(""); setPrecoConsolidadora(0);
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao registrar hospedagem";
      message.error(String(errMsg));
      console.error("[InserirHospedagem] ERRO →", e?.response?.status, e?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = (origemMilhas === "consolidadora" || milhasUtil > 0)
    && !!cartaoSel && hospedesSel.length > 0;

  const fmt = (v) => v > 0
    ? v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

  // fmt() esconde negativos de propósito (Saldo, Custo Médio etc. nunca são
  // negativos, então "0 ou vazio" é o comportamento certo ali). Mas Economia
  // Gerada/Economia % PODEM ser negativos (prejuízo) e isso precisa aparecer
  // na tela — senão o operador confirma uma operação achando que foi neutra.
  const fmtSigned = (v) => v !== 0
    ? v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

  // ── Styles ────────────────────────────────────────────────────────────
  const s = {
    page:   { minHeight: "100vh", background: "#f0f2f5", padding: "24px" },
    card:   { background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    header: { display: "flex", alignItems: "center", gap: 8, padding: "16px 20px 14px", borderBottom: "1px solid #f0f0f0" },
    body:   { padding: "20px 24px 24px" },
    lbl:    { fontSize: 12, color: "#555", fontWeight: 500, marginBottom: 4, display: "block" },
    inp:    { width: "100%", borderRadius: 4, border: "1px solid #d9d9d9", background: "#fafafa", height: 36, fontSize: 14 },
    dis:    { width: "100%", borderRadius: 4, border: "1px solid #e8e8e8", background: "#f5f5f5", height: 36, fontSize: 14, color: "#888" },
    fi:     { marginBottom: 14 },
    divider:{ borderTop: "1px dashed #e0e0e0", margin: "16px 0" },
    submitBtn: {
      width: "100%", height: 36,
      background: isFormValid ? "#1677ff" : "#bfbfbf",
      border: "none", borderRadius: 4, color: "#fff",
      fontSize: 13, fontWeight: 600, letterSpacing: "0.05em",
      cursor: isFormValid ? "pointer" : "not-allowed",
    },
  };

  const renderCamposOrigem = () => {
    if (origemMilhas === "balcao") return (
      <>
        <div style={s.fi}>
          <label style={s.lbl}>Nome do Fornecedor:</label>
          <Input size="middle" style={s.inp} placeholder="Nome do fornecedor"
            value={nomeFornecedor} onChange={(e) => setNomeFornecedor(e.target.value)} />
        </div>
        <Row gutter={10} style={{ marginBottom: 14 }}>
          <Col xs={14}>
            <label style={s.lbl}>Contato do Fornecedor:</label>
            <Input size="middle" style={s.inp} placeholder="(XX) XXXXX-XXXXX"
              value={contatoFornecedor} onChange={(e) => setContatoFornecedor(e.target.value)}
              suffix={<span style={{ color: "#bbb", fontSize: 13 }}>📞</span>} />
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>Campo opcional.</div>
          </Col>
          <Col xs={10}>
            <label style={s.lbl}>Custo do Milheiro</label>
            <InputNumber size="middle" style={s.inp} min={0} controls={false} placeholder="0,00"
              precision={2}
              value={custoMilheiroBalcao || undefined}
              onChange={(v) => setCustoMilheiroBalcao(Number(v) || 0)}
              formatter={fmtBRL}
              parser={parseBRL}
              suffix={<span style={{ color: "#bbb" }}>$</span>} />
          </Col>
        </Row>
        <div style={s.divider} />
      </>
    );

    if (origemMilhas === "consolidadora") return (
      <>
        <Row gutter={10} style={{ marginBottom: 14 }}>
          <Col xs={16}>
            <label style={s.lbl}>Nome da Consolidadora:</label>
            <Input size="middle" style={s.inp} placeholder="Nome da consolidadora"
              value={nomeConsolidadora} onChange={(e) => setNomeConsolidadora(e.target.value)} />
          </Col>
          <Col xs={8}>
            <label style={s.lbl}>Preço da Consolidadora</label>
            <InputNumber size="middle" style={s.inp} min={0} controls={false} placeholder="0,00"
              precision={2}
              value={precoConsolidadora || undefined}
              onChange={(v) => setPrecoConsolidadora(Number(v) || 0)}
              formatter={fmtBRL}
              parser={parseBRL}
              suffix={<span style={{ color: "#bbb" }}>$</span>} />
          </Col>
        </Row>
        <div style={s.divider} />
      </>
    );

    return null;
  };

  return (
    <div style={s.page}>
      <Spin spinning={loading}>
        <div style={s.card}>
          {/* Header */}
          <div style={s.header}>
            <span style={{ fontSize: 16 }}>🏨</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>Emissão de Hospedagem</span>
          </div>

          <div style={s.body}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}
              initialValues={{ dataEmissao: dayjs(), objetivoEmissao: "uso_proprio" }}
            >
              <Row gutter={24}>
                {/* ── COLUNA ESQUERDA ── */}
                <Col xs={24} md={13} style={{ borderRight: "1px dashed #e0e0e0", paddingRight: 24 }}>

                  {/* Origem das Milhas */}
                  <div style={s.fi}>
                    <label style={s.lbl}>Origem das Milhas:</label>
                    <Form.Item name="origemMilhas" noStyle initialValue="proprias">
                      <Select size="middle" style={{ width: 220 }} onChange={handleOrigemChange}>
                        <Option value="proprias">Milhas Próprias</Option>
                        <Option value="balcao">Balcão de Milhas</Option>
                        <Option value="consolidadora">Consolidadora</Option>
                      </Select>
                    </Form.Item>
                  </div>

                  {renderCamposOrigem()}

                  {/* Titular + Saldo + Custo Médio + CPFs Livres */}
                  {origemMilhas !== "consolidadora" ? (
                    <Row gutter={10} style={{ marginBottom: 14 }}>
                      <Col xs={24} sm={10}>
                        <label style={s.lbl}>Titular/Conta da Operação</label>
                        <Form.Item name="cartaoId" noStyle rules={[{ required: true, message: "Selecione" }]}>
                          <Select
                            size="middle" style={{ width: "100%" }}
                            placeholder="Selecione o programa"
                            loading={loading}
                            onChange={handleCartaoChange}
                          >
                            {cartoes.map((c) => (
                              <Option key={c.car_id} value={c.car_id}>
                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <LogoCircle nome={c.car_nome_programa} size={20} />
                                  {(c.cli_nome || "").split(" ")[0]} - {c.car_nome_programa}
                                </span>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={8} sm={5}>
                        <label style={s.lbl}>Saldo disponível</label>
                        <Input size="middle" style={s.dis} readOnly
                          value={saldoDisp > 0 ? saldoDisp.toLocaleString("pt-BR") : ""}
                          placeholder="0"
                          suffix={<span style={{ color: "#bbb" }}>#</span>}
                        />
                      </Col>
                      <Col xs={8} sm={5}>
                        <label style={s.lbl}>Custo Médio</label>
                        <Input size="middle" style={s.dis} readOnly
                          value={custoMedio > 0 ? fmt(custoMedio) : ""}
                          placeholder="0,00"
                          suffix={<span style={{ color: "#bbb" }}>$</span>}
                        />
                      </Col>
                      <Col xs={8} sm={4}>
                        <label style={s.lbl}>CPFs Livres</label>
                        <Input size="middle" style={s.dis} readOnly
                          value={cartaoSel ? cpfsLivres : ""} placeholder="0"
                          suffix={<span style={{ color: "#bbb" }}>#</span>}
                        />
                      </Col>
                    </Row>
                  ) : (
                    <div style={s.fi}>
                      <label style={s.lbl}>Titular/Conta da Operação</label>
                      <Form.Item name="cartaoId" noStyle rules={[{ required: true, message: "Selecione" }]}>
                        <Select size="middle" style={{ width: "100%" }} placeholder="Selecione o programa"
                          loading={loading} onChange={handleCartaoChange}>
                          {cartoes.map((c) => (
                            <Option key={c.car_id} value={c.car_id}>
                              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <LogoCircle nome={c.car_nome_programa} size={20} />
                                {(c.cli_nome || "").split(" ")[0]} - {c.car_nome_programa}
                              </span>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  )}

                  <div style={s.divider} />

                  {/* Preço da Reserva Pagante + Print Recibo */}
                  <Row gutter={10} style={{ marginBottom: 4 }}>
                    <Col xs={14} sm={14}>
                      <label style={s.lbl}>Preço da Reserva Pagante</label>
                      <Input
                        size="middle"
                        style={s.inp}
                        placeholder="0,00"
                        value={precoReservaPagante === 0 ? '' : precoReservaPagante.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, '');
                          if (raw === '') { setPrecoReservaPagante(0); return; }
                          const num = parseInt(raw, 10) / 100;
                          setPrecoReservaPagante(num);
                        }}
                        suffix={<span style={{ color: "#bbb" }}>$</span>}
                      />
                    </Col>
                    <Col xs={10} sm={10}>
                      <label style={s.lbl}>&nbsp;</label>
                      <Upload
                        fileList={fileList}
                        beforeUpload={(file) => {
                          const formData = new FormData();
                          formData.append("file", file);
                          api.post("/upload/images", formData, {
                            headers: { "Content-Type": "multipart/form-data" },
                          }).catch(() => {});
                          setFileList([file]);
                          return false;
                        }}
                        maxCount={1}
                        showUploadList={false}
                        accept="image/*,.pdf"
                      >
                        <Button
                          icon={<PaperClipOutlined />}
                          style={{ width: "100%", height: 36, border: "1px solid #d9d9d9", borderRadius: 4, background: "#fafafa", fontSize: 13 }}
                        >
                          PRINT RECIBO
                        </Button>
                      </Upload>
                      <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                        {fileList.length > 0 ? fileList[0].name : "Nenhum arquivo selecionado."}
                      </div>
                    </Col>
                  </Row>

                  {/* Milhas Utilizadas — não se aplica à Consolidadora */}
                  {origemMilhas !== "consolidadora" && (
                    <div style={s.fi}>
                      <label style={s.lbl}>Milhas Utilizadas</label>
                      <InputNumber
                        size="middle" style={s.inp} min={0} controls={false}
                        onChange={(v) => setMilhasUtil(Number(v) || 0)}
                        formatter={fmtMilhas}
                        parser={parseMilhas}
                        suffix={<span style={{ color: "#bbb" }}>#</span>}
                      />
                    </div>
                  )}

                  {/* Preço em Milhas (calculado) — não se aplica à Consolidadora */}
                  {origemMilhas !== "consolidadora" && (
                    <div style={s.fi}>
                      <label style={s.lbl}>Preço em Milhas</label>
                      <Input size="middle" style={s.dis} readOnly
                        value={fmt(precoEmMilhas)}
                        placeholder="0,00"
                        suffix={<span style={{ color: "#bbb" }}>$</span>}
                      />
                    </div>
                  )}

                  {/* Taxas + Extras */}
                  <Row gutter={10} style={{ marginBottom: 14 }}>
                    <Col xs={12}>
                      <label style={s.lbl}>Taxas</label>
                      <InputNumber
                        size="middle" style={s.inp} min={0} controls={false}
                        precision={2}
                        onChange={(v) => setTaxas(Number(v) || 0)}
                        formatter={fmtBRL}
                        parser={parseBRL}
                        placeholder="0,00"
                        suffix={<span style={{ color: "#bbb" }}>$</span>}
                      />
                    </Col>
                    <Col xs={12}>
                      <label style={s.lbl}>Extras</label>
                      <InputNumber
                        size="middle" style={s.inp} min={0} controls={false}
                        precision={2}
                        onChange={(v) => setExtras(Number(v) || 0)}
                        formatter={fmtBRL}
                        parser={parseBRL}
                        placeholder="0,00"
                        suffix={<span style={{ color: "#bbb" }}>$</span>}
                      />
                    </Col>
                  </Row>

                  {/* Economia Gerada + Economia % */}
                  <Row gutter={10} style={{ marginBottom: 14 }}>
                    <Col xs={12}>
                      <label style={s.lbl}>Economia Gerada</label>
                      <Input size="middle" style={s.dis} readOnly
                        value={fmtSigned(economiaGerada)} placeholder="0,00"
                        suffix={<span style={{ color: "#bbb" }}>$</span>}
                      />
                    </Col>
                    <Col xs={12}>
                      <label style={s.lbl}>Economia em %</label>
                      <Input size="middle" style={s.dis} readOnly
                        value={fmtSigned(economiaPct)}
                        placeholder="0"
                        suffix={<span style={{ color: "#bbb" }}>%</span>}
                      />
                    </Col>
                  </Row>

                  {/* Voucher da Reserva */}
                  <div style={s.fi}>
                    <label style={s.lbl}>Voucher da Reserva</label>
                    <Form.Item name="voucherReserva" noStyle rules={[{ required: true, message: "Campo obrigatório." }]}>
                      <Input size="middle" style={s.inp} />
                    </Form.Item>
                  </div>

                  {/* Data do Check-In + Data do Check-Out */}
                  <Row gutter={10} style={{ marginBottom: 14 }}>
                    <Col xs={12}>
                      <label style={s.lbl}>Data do Check-In</label>
                      <Form.Item name="dataCheckIn" noStyle>
                        <DatePicker style={s.inp} format="DD/MM/YYYY" size="middle" />
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <label style={s.lbl}>Data do Check-Out</label>
                      <Form.Item name="dataCheckOut" noStyle>
                        <DatePicker style={s.inp} format="DD/MM/YYYY" size="middle" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Nome da Hospedagem */}
                  <div style={s.fi}>
                    <label style={s.lbl}>Nome da Hospedagem</label>
                    <Form.Item name="nomeHospedagem" noStyle>
                      <Input size="middle" style={s.inp} />
                    </Form.Item>
                  </div>

                  {/* Data da Emissão */}
                  <div style={s.fi}>
                    <label style={s.lbl}>Data da Emissão</label>
                    <Form.Item name="dataEmissao" noStyle>
                      <DatePicker style={s.inp} format="DD/MM/YYYY" size="middle"
                        disabledDate={(c) => c && c > dayjs().endOf("day")}
                      />
                    </Form.Item>
                  </div>

                  {/* Objetivo da Emissão */}
                  <div style={s.fi}>
                    <label style={s.lbl}>Objetivo da Emissão</label>
                    <Form.Item name="objetivoEmissao" noStyle>
                      <Select size="middle" style={{ width: "100%" }}>
                        <Option value="uso_proprio">Uso Próprio</Option>
                        <Option value="presente">Presente</Option>
                        <Option value="venda">Venda</Option>
                        <Option value="outro">Outro</Option>
                      </Select>
                    </Form.Item>
                  </div>

                  {/* Observações */}
                  <div style={s.fi}>
                    <label style={s.lbl}>Observações:</label>
                    <Form.Item name="observacoes" noStyle>
                      <Input.TextArea rows={4} style={{ width: "100%", borderRadius: 4, border: "1px solid #d9d9d9", fontSize: 13, resize: "vertical" }} />
                    </Form.Item>
                  </div>
                </Col>

                {/* ── COLUNA DIREITA: Hóspedes ── */}
                <Col xs={24} md={11} style={{ paddingLeft: 24 }}>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>Registro de Hóspedes</span>

                    {/* Input de nome do hóspede */}
                    <div style={{ marginTop: 12, marginBottom: 4 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Input
                          size="middle"
                          placeholder="Digite o nome do hóspede"
                          value={nomeHospedeInput}
                          onChange={(e) => {
                            setNomeHospedeInput(e.target.value);
                            if (e.target.value) setErroHospede(false);
                          }}
                          onKeyDown={handleHospedeKeyDown}
                          style={{ borderRadius: 4, flex: 1, borderColor: erroHospede ? "#ff4d4f" : undefined }}
                          suffix={
                            <PlusOutlined
                              style={{ color: "#1677ff", cursor: "pointer", fontSize: 16 }}
                              onClick={adicionarHospede}
                            />
                          }
                        />
                      </div>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                        Pressione 'Enter' ou clique no <PlusOutlined style={{ fontSize: 10 }} /> para adicionar.
                      </div>
                      <div style={{ fontSize: 11, color: "#1677ff", marginTop: 2 }}>
                        Não é necessário um registro prévio do hóspede!
                      </div>
                      {erroHospede && (
                        <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 4 }}>
                          Inclua pelo menos um hóspede.
                        </div>
                      )}
                    </div>

                    {/* Header tabela hóspedes */}
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", padding: "8px 0 8px", borderBottom: "1px solid #f0f0f0", marginTop: 12, marginBottom: 8 }}>
                      Nome
                    </div>

                    {/* Lista de hóspedes ou placeholder */}
                    <div style={{ maxHeight: 280, overflowY: "auto" }}>
                      {hospedesSel.length === 0 ? (
                        <div style={{
                          padding: "20px 14px", fontSize: 12, color: "#aaa",
                          background: "#fafafa", borderRadius: 4, textAlign: "center",
                          border: "1px dashed #e0e0e0",
                        }}>
                          Utilize o campo acima para adicionar os nomes dos hóspedes.
                        </div>
                      ) : (
                        hospedesSel.map((h) => (
                          <div
                            key={h.id}
                            style={{
                              padding: "8px 10px", marginBottom: 6, borderRadius: 4,
                              background: "#f9f9f9", border: "1px solid #f0f0f0",
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}
                          >
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>{h.nome}</span>
                            <button
                              type="button"
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", padding: "0 4px", fontSize: 14 }}
                              onClick={() => removerHospede(h.id)}
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Resumo hóspedes */}
                    <div style={{ marginTop: 16, padding: "10px 12px", background: "#f9f9f9", borderRadius: 4, border: "1px solid #f0f0f0" }}>
                      <div style={{ fontSize: 12, color: "#555" }}>CPFs limitados pelo programa: <strong>{cpfsLivres}</strong></div>
                      <div style={{ fontSize: 12, color: "#555" }}>CPFs que já voaram: <strong>0</strong></div>
                      <div style={{ fontSize: 12, color: "#555" }}>Hóspedes desta reserva: <strong>{hospedesSel.length}</strong></div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Submit */}
              <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
                <button type="submit" style={s.submitBtn} disabled={saving || !isFormValid}>
                  {saving ? "PROCESSANDO..." : isFormValid ? "CONFIRMAR HOSPEDAGEM" : "PREENCHA OS DADOS!"}
                </button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </Spin>
    </div>
  );
}